import { DepositTotal } from "./deposit-total";
import { BorrowAprBreakdown } from "./borrow-apr-breakdown";
import { DepositAprBreakdown } from "./deposit-apr-breakdown";
import { asApr } from "@silo/utils";
import { BorrowTotal } from "./borrow-total";
import {
  ConfigAssetPoints,
  getPointsLabel,
  LINKS,
  MerklRewardLegacy,
  PERCENT_ZERO,
} from "@silo/types";
import {
  getBorrowAprMetrics,
  getBorrowState,
  getBorrowStateColor,
  getDepositAprMetrics,
} from "./utils";
import { Tooltip, TooltipContent, TooltipOrigin } from "../tooltip";
import { FireIcon, HelpIcon, SuperfestIcon } from "../../icons";
import { classNames } from "../../utils/class-names";
import { ExternalLink } from "../link";
import { Perk } from "../perk";
import { PointsLinks } from "../perk/points-links";

export interface MarketRowProps {
  depositApr: bigint;
  depositRewardsApr: bigint;
  borrowApr: bigint;
  borrowRewardsApr: bigint;
  symbol: string;
  rewardsSymbol: string;
  rewardsLink: string;
  isBorrowAprFixed: boolean;
  isProtectedDeposit: boolean;
  merklRewards: MerklRewardLegacy[];
  points: ConfigAssetPoints;
}

export function MarketRow({
  depositApr,
  depositRewardsApr,
  borrowApr,
  borrowRewardsApr,
  symbol,
  rewardsSymbol,
  rewardsLink,
  isBorrowAprFixed,
  isProtectedDeposit,
  merklRewards,
  points,
}: MarketRowProps): JSX.Element {
  const { hasDepositRewardsApr, totalDepositApr, depositRewards } =
    getDepositAprMetrics({
      depositApr,
      depositRewardsApr,
      isProtected: isProtectedDeposit,
      rewardsSymbol,
      merklRewards,
    });

  const { hasBorrowRewardsApr, totalBorrowApr, borrowRewards } =
    getBorrowAprMetrics({
      merklRewards,
      rewardsSymbol,
      borrowApr,
      borrowRewardsApr,
    });

  const hasAnyRewards = hasDepositRewardsApr || hasBorrowRewardsApr;

  const borrowState = getBorrowState(hasBorrowRewardsApr, totalBorrowApr);
  const borrowStateColor = getBorrowStateColor(borrowState);

  const collateralPoints = points.collateral ?? [];
  const protectedPoints = points.protected ?? [];
  const depositPoints = isProtectedDeposit ? protectedPoints : collateralPoints;
  const debtPoints = points.debt ?? [];
  const allPoints = [...depositPoints, ...debtPoints];

  const hasPoints = allPoints.length > 0;
  const pointsKeys = [...new Set(allPoints.map((item) => item._tag))];
  const hasSuperfestRewards = merklRewards.some((reward) => reward.isSuperfest);
  const hasSignals = hasPoints || hasSuperfestRewards;

  const isTooltipOn = hasAnyRewards || hasSignals;

  return (
    <Tooltip off={!isTooltipOn}>
      <TooltipOrigin>
        <div className="flex flex-col gap-2.5 items-end">
          <div className="flex items-center gap-2.5">
            {isTooltipOn && <HelpIcon className="w-4 h-4 text-shale-300" />}

            <div className="flex items-center gap-1 font-mono text-sm font-medium text-white">
              <span
                data-cy="top"
                className={hasDepositRewardsApr ? "text-amber-300" : ""}
              >
                {asApr(totalDepositApr)}
              </span>
              <span>/</span>
              <span
                data-cy="bottom"
                className={classNames(
                  isBorrowAprFixed ? "text-amber-300" : "",
                  borrowStateColor
                )}
              >
                {asApr(totalBorrowApr)}
              </span>

              {borrowState === "netYield" && (
                <FireIcon className="w-4 h-4 text-green-400" />
              )}
            </div>
          </div>

          {hasSignals && (
            <Perk.SignalsRoot>
              {hasSuperfestRewards && (
                <SuperfestIcon className={Perk.iconClassName} />
              )}

              {hasPoints &&
                pointsKeys.map((pointsKey) => (
                  <Perk.PointsIcon
                    className={Perk.iconClassName}
                    pointsKey={pointsKey}
                  />
                ))}
            </Perk.SignalsRoot>
          )}
        </div>
      </TooltipOrigin>
      <TooltipContent>
        <dl className="flex flex-col gap-7">
          <div className="flex flex-col gap-2.5">
            <dt className="font-medium text-white">
              {symbol} total deposit APR
            </dt>

            <DepositTotal
              depositApr={depositApr}
              depositRewardsApr={depositRewardsApr}
              isProtected={isProtectedDeposit}
              rewardsSymbol={rewardsSymbol}
              merklRewards={merklRewards}
              revenueApr={PERCENT_ZERO}
            />
          </div>

          {hasDepositRewardsApr && (
            <div className="flex flex-col gap-5">
              <DepositAprBreakdown
                depositApr={depositApr}
                rewards={depositRewards}
                isProtected={isProtectedDeposit}
                revenueApr={PERCENT_ZERO}
              />

              <ExternalLink size="small" href={rewardsLink}>
                Rewards details
              </ExternalLink>

              {hasSuperfestRewards && (
                <ExternalLink size="small" href={LINKS.SUPERFEST}>
                  SuperFest details
                </ExternalLink>
              )}
            </div>
          )}

          {depositPoints.length > 0 && (
            <div className="flex flex-col gap-5">
              <Perk.Root>
                <Perk.Label />

                {depositPoints.map((pointsItem) => (
                  <Perk.Item>
                    <Perk.PointsIcon
                      pointsKey={pointsItem._tag}
                      className={Perk.iconClassName}
                    />

                    <Perk.Emphasis>{getPointsLabel(pointsItem)}</Perk.Emphasis>

                    <Perk.PointsLabel />
                  </Perk.Item>
                ))}
              </Perk.Root>

              <PointsLinks pointsItems={depositPoints} />
            </div>
          )}

          <div className="h-px bg-shale-600"></div>

          <div className="flex flex-col gap-2.5">
            <dt className="font-medium text-white">
              {symbol} total borrow APR
            </dt>

            <BorrowTotal
              hasBorrowRewards={hasBorrowRewardsApr}
              totalBorrowApr={totalBorrowApr}
              isBorrowAprFixed={isBorrowAprFixed}
            />
          </div>

          {hasBorrowRewardsApr && (
            <div className="flex flex-col gap-5">
              <BorrowAprBreakdown
                borrowApr={borrowApr}
                borrowState={borrowState}
                variant="market"
                rewards={borrowRewards}
              />

              <ExternalLink size="small" href={rewardsLink}>
                Rewards details
              </ExternalLink>
            </div>
          )}

          {debtPoints.length > 0 && (
            <div className="flex flex-col gap-5">
              <Perk.Root>
                <Perk.Label />

                {debtPoints.map((pointsItem) => (
                  <Perk.Item>
                    <Perk.PointsIcon
                      pointsKey={pointsItem._tag}
                      className={Perk.iconClassName}
                    />

                    <Perk.Emphasis>{getPointsLabel(pointsItem)}</Perk.Emphasis>

                    <Perk.PointsLabel />
                  </Perk.Item>
                ))}
              </Perk.Root>

              <PointsLinks pointsItems={debtPoints} />
            </div>
          )}
        </dl>
      </TooltipContent>
    </Tooltip>
  );
}
